// Conversation simulator — chat with your own bot inside the editor. The bot
// runs the full real pipeline (orchestrator, KB, classify, generate, handoff);
// its replies are sinked server-side (never reach WhatsApp). Lets an owner test
// a flow + knowledge base without messaging the number from a phone.
//
// The thread PERSISTS: opening the panel continues the existing conversation
// (so it's reviewable / not volatile). "Reiniciar" is the only thing that wipes
// it. When the flow hands off to a person the conversation dead-ends (in
// production a human takes over from the inbox); the panel shows that clearly
// and offers "Devolver al agente" to keep testing the loop.

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import type {
  SimulatorApi,
  SimulatorLead,
  SimulatorMessage,
  SimulatorToolFailure,
} from './flows-data.js'
import { Button, Input, MessageBubble, MessageThread, Spinner } from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

const POLL_MS = 1500
const MAX_POLLS = 24 // ~36s ceiling before we stop waiting on a reply

const HANDOFF_STATES = new Set(['waiting_handoff', 'active_handoff'])

/** Why a turn produced no reply (or an unexpected one), in the author's words.
 *  Every one of these used to render as nothing at all: the typing indicator
 *  vanished and the thread sat there, so a paused agent, a suspended account, a
 *  blocked consent gate and a crashed pipeline were indistinguishable from a bot
 *  that simply had nothing to say (recon R0 Q6, silent classes 1 and 2). */
function turnStateLine(outcome: string, reason: string | null): string | null {
  switch (outcome) {
    case 'worker_paused':
      return 'El agente está pausado: no respondió. Actívalo en Ajustes para seguir probando.'
    case 'tenant_suspended':
      return 'La cuenta está suspendida: el agente no responde.'
    case 'contact_suppressed':
      return 'Este contacto está dado de baja o bloqueado: el agente no responde.'
    case 'short_circuit_already_handoff':
      return 'La conversación está con una persona: el agente no responde.'
    case 'already_processed':
      return 'Este mensaje ya se había procesado: no se generó una respuesta nueva.'
    case 'consent_blocked':
      return `El aviso de privacidad bloquea la conversación (${consentReason(reason)}): el agente no puede responder.`
    case 'consent_declined':
      return 'La persona no aceptó el aviso de privacidad: la conversación se cierra sin respuesta.'
    case 'consent_reprompted':
      return 'El agente volvió a pedir la aceptación del aviso de privacidad.'
    case 'out_of_hours':
      return 'Fuera de horario: el agente respondió con el mensaje de fuera de horario y pasó la conversación con una persona.'
    case 'opted_out':
      return 'La persona pidió no recibir más mensajes: el agente dejó de responder.'
    case 'failed':
      return `El procesamiento falló: ${reason ?? 'error desconocido'}. Revisa la configuración del flujo.`
    default:
      return null
  }
}

/** The consent gate's machine reasons, said plainly. */
function consentReason(reason: string | null): string {
  switch (reason) {
    case 'refused':
      return 'la persona lo rechazó'
    case 'revoked':
      return 'la persona revocó su consentimiento'
    case 'cooling_down':
      return 'hay que esperar antes de volver a preguntar'
    case 'abuse_cap':
      return 'se alcanzó el límite de intentos'
    default:
      return reason ?? 'motivo desconocido'
  }
}

/** Why the flow handed the conversation to a person. The card below the thread
 *  already says THAT it happened; an author also needs to know why. */
function handoffReasonLine(outcome: string, reason: string | null): string | null {
  switch (outcome) {
    case 'guard_handoff':
      return `Motivo: la respuesta no pasó una validación (${reason ?? 'sin detalle'}).`
    case 'no_kb_match_handoff':
      return 'Motivo: no hay información en la base de conocimiento para responder.'
    case 'opposition_handoff':
      return 'Motivo: la persona pidió no ser atendida por un agente automático.'
    case 'priority_trigger_handoff':
      return 'Motivo: se activó una regla de atención prioritaria.'
    case 'non_text_handoff':
      return `Motivo: el mensaje no es texto (${reason ?? 'archivo'}) y necesita a una persona.`
    case 'handoff_check_handoff':
    case 'skill_handoff':
      return `Motivo: ${reason ?? 'el flujo lo indicó'}.`
    default:
      return null
  }
}

/** An authored tool that failed, in the author's words (F42). */
function toolFailureLine(f: SimulatorToolFailure): string {
  const where = f.nodeId ? ` en el paso "${f.nodeId}"` : ''
  switch (f.error) {
    case 'unknown_tool':
      return `La herramienta "${f.toolName}"${where} no existe.`
    case 'arg_parse_failed':
      return `Los datos que el flujo le pasa a "${f.toolName}"${where} no son válidos.`
    default:
      return `La herramienta "${f.toolName}"${where} falló al ejecutarse.`
  }
}

function hhmm(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const countOutbound = (msgs: SimulatorMessage[]): number =>
  msgs.filter((m) => m.direction === 'outbound').length

export function SimulatorPanel({
  api,
  boardHref,
}: {
  api: SimulatorApi
  /** Builds the funnel board's deep link for one process. Absent on the
   *  operator twin, where /prospectos is not a route staff can open. */
  boardHref?: (pipelineId: string) => string
}): JSX.Element {
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<SimulatorMessage[]>([])
  const [convState, setConvState] = useState<string>('pending')
  // Where the transfer landed. Null = la cola. Only meaningful while `isHandoff`.
  const [assignedTo, setAssignedTo] = useState<{ id: string; name: string } | null>(null)
  // How the last turn ended + what its tool calls did. Both drive honest state
  // lines instead of an empty thread.
  const [lastTurn, setLastTurn] = useState<{
    outcome: string
    reason: string | null
    at: string
  } | null>(null)
  const [toolFailures, setToolFailures] = useState<SimulatorToolFailure[]>([])
  // Where this rehearsal's card ended up. Re-read every turn, so the line
  // follows the card as the flow advances it.
  const [lead, setLead] = useState<SimulatorLead | null>(null)
  const [draft, setDraft] = useState('')
  const [starting, setStarting] = useState(true)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  // Guards against a late poll from a previous send overwriting newer state.
  const pollToken = useRef(0)

  const isHandoff = HANDOFF_STATES.has(convState)

  /** Apply a polled thread payload to local state. */
  function applyThread(thread: {
    messages: SimulatorMessage[]
    state: string
    assignedTo?: { id: string; name: string } | null
    lastTurn?: { outcome: string; reason: string | null; at: string } | null
    toolFailures?: SimulatorToolFailure[]
    lead?: SimulatorLead | null
  }): void {
    setMessages(thread.messages)
    setConvState(thread.state)
    setAssignedTo(thread.assignedTo ?? null)
    setLastTurn(thread.lastTurn ?? null)
    setToolFailures(thread.toolFailures ?? [])
    // Reiniciar deletes the sim contact's cards, so the poll right after a reset
    // answers null and the line clears itself — it can only ever name a card
    // this session produced.
    setLead(thread.lead ?? null)
  }

  // Open (continue the existing thread, no wipe) or reset (wipe) on demand.
  async function load(wipe: boolean): Promise<void> {
    setStarting(true)
    setError('')
    pollToken.current++ // cancel any in-flight poll
    setWaiting(false)
    try {
      const id = wipe ? await api.reset() : await api.open()
      setConversationId(id)
      const thread = await api.messages(id)
      applyThread(thread)
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo iniciar la prueba.'))
    } finally {
      setStarting(false)
    }
  }

  useEffect(() => {
    void load(false)
  }, [])

  // Auto-scroll to the newest message. (Assign scrollTop rather than call
  // scrollTo, which jsdom doesn't implement.)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages, waiting])

  async function send(): Promise<void> {
    const text = draft.trim()
    if (text === '' || !conversationId || waiting || isHandoff) return
    setDraft('')
    setError('')
    const baseOutbound = countOutbound(messages)
    // The turn we're superseding. A silent class produces no outbound at all, so
    // "a new turn was recorded" is the honest completion signal; without it those
    // turns burned the full 36s ceiling before showing a generic apology.
    const baseTurnAt = lastTurn?.at ?? null
    // Optimistically show the customer's message.
    setMessages((m) => [
      ...m,
      {
        id: `local-${m.length}`,
        direction: 'inbound',
        sender_type: 'contact',
        content: text,
        status: null,
        created_at: new Date().toISOString(),
      },
    ])
    setWaiting(true)
    const token = ++pollToken.current
    let replied = false
    try {
      await api.send(text)
      // Poll until a NEW outbound reply appears (a normal reply, or the handoff
      // phrase), or we hit the ceiling.
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, POLL_MS))
        if (token !== pollToken.current) return // a newer send/reset superseded us
        const thread = await api.messages(conversationId)
        applyThread(thread)
        const turnRecorded = thread.lastTurn != null && thread.lastTurn.at !== baseTurnAt
        if (countOutbound(thread.messages) > baseOutbound || turnRecorded) {
          replied = true
          break
        }
      }
      // The poll ceiling ran out with NOTHING recorded — no reply and no turn.
      // Now that every finished turn records its outcome, this means the job
      // never ran at all (worker stopped, queue not draining), which is a
      // genuinely different problem from the states rendered below the thread.
      if (!replied) {
        setError(
          'El agente no respondió. Revisa en Ajustes que el flujo principal exista y esté activo, o inténtalo de nuevo.',
        )
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo enviar el mensaje.'))
    } finally {
      if (token === pollToken.current) setWaiting(false)
    }
  }

  async function returnToBot(): Promise<void> {
    if (!conversationId) return
    setError('')
    try {
      await api.returnToBot(conversationId)
      const thread = await api.messages(conversationId)
      applyThread(thread)
    } catch (err) {
      setError(apiErrorMessage(err, 'No se pudo devolver al agente.'))
    }
  }

  const stateLine = lastTurn ? turnStateLine(lastTurn.outcome, lastTurn.reason) : null
  const handoffWhy = lastTurn ? handoffReasonLine(lastTurn.outcome, lastTurn.reason) : null

  if (starting) return <Spinner label="Preparando la prueba…" />

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11.5px] text-text-3">
          Escribe como lo haría un cliente. El agente responde igual que en WhatsApp, pero nada se
          envía de verdad. La conversación se guarda hasta que la reinicies.
        </p>
        <Button variant="ghost" size="sm" onClick={() => void load(true)} disabled={waiting}>
          Reiniciar
        </Button>
      </div>

      <div ref={scrollRef} className="max-h-[360px] overflow-y-auto">
        <MessageThread label="Conversación de prueba">
          {messages.length === 0 && !waiting ? (
            <p className="m-auto max-w-[280px] py-8 text-center text-[12px] text-text-3">
              Aún no hay mensajes. Saluda o pregunta algo para ver cómo responde tu agente.
            </p>
          ) : null}
          {messages.map((m) =>
            m.direction === 'inbound' ? (
              <MessageBubble key={m.id} sender="user" author="Tú" time={hhmm(m.created_at)}>
                {m.content ?? ''}
              </MessageBubble>
            ) : (
              <MessageBubble key={m.id} sender="bot" time={hhmm(m.created_at)}>
                {m.content ?? ''}
              </MessageBubble>
            ),
          )}
          {waiting ? (
            <div className="mr-auto mb-3.5 rounded-[14px] rounded-br-[5px] border border-line bg-surface px-3 py-[9px] text-[12.5px] text-text-3">
              El agente está escribiendo<span className="animate-pulse">…</span>
            </div>
          ) : null}
        </MessageThread>
      </div>

      {/* F6-1 — dónde quedó la tarjeta. La captura siempre escribió; lo que
          faltaba era decir en qué proceso, porque una conversación que entra a
          otro proceso por significado deja el tablero que estás mirando igual
          de vacío que un simulador que no guarda nada (CP-5'). */}
      {lead ? (
        <p
          className="rounded-xl border border-line bg-surface-2 p-3 text-[12px] text-text-2"
          data-testid="sim-lead-line"
        >
          Tu tarjeta quedó en{' '}
          {boardHref ? (
            <Link
              to={boardHref(lead.pipelineId)}
              className="font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              {lead.pipelineName} · {lead.stageName}
            </Link>
          ) : (
            <span className="font-semibold text-text-1">
              {lead.pipelineName} · {lead.stageName}
            </span>
          )}
        </p>
      ) : null}

      {/* The last turn's outcome, when it needs explaining. Same card pattern as
          the handoff block below so the panel reads as one surface. */}
      {stateLine ? (
        <p
          className="rounded-xl border border-line bg-surface-2 p-3 text-[12px] text-text-2"
          data-testid="sim-state-line"
        >
          {stateLine}
        </p>
      ) : null}

      {/* Tool failures are the AUTHOR's business, not the customer's: the
          customer-side thread above is exactly what the runtime produced. */}
      {toolFailures.length > 0 ? (
        <div
          className="flex flex-col gap-2 rounded-xl border border-line bg-surface-2 p-3 text-[12px] text-text-2"
          data-testid="sim-tool-failures"
        >
          <p className="font-medium text-text-1">Una herramienta del flujo falló</p>
          {toolFailures.map((f, i) => (
            // The index is part of the key on purpose: one node can fail the
            // same tool more than once in a turn, and tool+node alone gave
            // those rows identical keys.
            <div
              key={`${f.toolName}-${f.nodeId ?? 'sin-nodo'}-${i}`}
              className="flex flex-col gap-0.5"
            >
              <p>{toolFailureLine(f)}</p>
              {f.detail ? <p className="text-[11.5px] text-text-3">{f.detail}</p> : null}
            </div>
          ))}
        </div>
      ) : null}

      {isHandoff ? (
        <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface-2 p-3 text-[12px] text-text-2">
          <p>
            El agente pasó la conversación <strong>con una persona</strong>. En producción, un
            asesor la continúa desde la bandeja de entrada. Para seguir probando el agente,
            devuélvesela o reinicia.
          </p>
          {/* Where it landed. An author who chose "Automático" needs to see the
              answer the espacio gave, not just that a transfer happened: a rule
              that silently fell through to la cola looks identical otherwise. */}
          {/* WHY it was transferred. "Pasó con una persona" reads identically
              whether the flow chose it or a guard forced it. */}
          {handoffWhy ? <p data-testid="sim-handoff-why">{handoffWhy}</p> : null}
          <p className="font-medium text-text-1">
            {assignedTo ? `→ asignado a ${assignedTo.name}` : '→ a la cola (sin asignar)'}
          </p>
          <div>
            <Button size="sm" variant="secondary" onClick={() => void returnToBot()}>
              Devolver al agente
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="text-[12px] text-sens" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-end gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
          placeholder={isHandoff ? 'Conversación con una persona…' : 'Escribe un mensaje…'}
          aria-label="Mensaje de prueba"
          className="flex-1"
          disabled={!conversationId || isHandoff}
        />
        <Button
          onClick={() => void send()}
          disabled={!conversationId || draft.trim() === '' || waiting || isHandoff}
        >
          Enviar
        </Button>
      </div>
    </div>
  )
}
